import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { initializeApp, getApps, getApp, deleteApp, FirebaseError } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, getIdToken, onIdTokenChanged, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { SolutionTokenService } from './solution-token.service';
import { WorkspaceAccessService, WorkspaceAccesible } from './workspace-access.service';
import { CAPACIDAD_POR_RUTA, rutaInicial } from '../guards/capability.guard';
import { mensajeDeErrorDeLogin, MensajeTraducible } from '../utils/mensaje-de-error-de-login';

export const GENERIC_WORKSPACE_ID = '00000000-0000-0000-0000-000000000000';

export interface UserRole {
  id: string;
  name: string;
}

export interface WorkspaceInfo {
  id: string;
  name: string;
  workspaceCode: string;
  workspaceType?: number;
}

export interface LoginUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  roleId: string;
  phone: string;
  workspaceId?: string;
  workspaces?: WorkspaceInfo[];
  /** Viene en la respuesta del login; la foto del avatar sale de acá. */
  profile?: { profilePhotoUrl?: string | null } | null;
}

export interface LoginResponse {
  token: string;
  user: LoginUser;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly API = `${environment.apiUrl}/auth`;
  private http = inject(HttpClient);
  private router = inject(Router);
  private solutionToken = inject(SolutionTokenService);
  private workspaceAccess = inject(WorkspaceAccessService);

  private _token = signal<string | null>(localStorage.getItem('token') || sessionStorage.getItem('token'));
  private _user = signal<LoginUser | null>(
    JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || 'null'),
  );

  private _workspaceId = signal<string>(
    this._user()?.workspaceId || localStorage.getItem('workspaceId') || sessionStorage.getItem('workspaceId') || GENERIC_WORKSPACE_ID
  );

  private _loading = signal(false);
  private _error = signal<MensajeTraducible | null>(null);

  readonly token = this._token.asReadonly();
  readonly user = this._user.asReadonly();
  readonly workspaceId = this._workspaceId.asReadonly();
  /**
   * Si el workspace opera flota. Sale del perfil del workspace que manda el
   * backend con el token (el `type` de su metadataJson en Console); antes era
   * una lista de ids en `environment` que obligaba a hacer build por cliente.
   */
  readonly isVehicleWorkspace = computed(() => this.solutionToken.activosComoVehiculos());
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());

  constructor() {
    // Session token is now managed completely by SolutionTokenService interceptor
  }

  private get storage(): Storage {
    return localStorage.getItem('token') ? localStorage : sessionStorage;
  }

  toggleWorkspace(): void {
    const userWorkspaces = this._user()?.workspaces || [];
    if (userWorkspaces.length > 1) {
      const currentIndex = userWorkspaces.findIndex(w => w.id === this._workspaceId());
      const nextIndex = (currentIndex + 1) % userWorkspaces.length;
      const nextWorkspace = userWorkspaces[nextIndex].id;
      this._workspaceId.set(nextWorkspace);
      this.storage.setItem('workspaceId', nextWorkspace);

      if (this._user()) {
        const updatedUser = { ...this._user()!, workspaceId: nextWorkspace };
        this._user.set(updatedUser);
        this.storage.setItem('user', JSON.stringify(updatedUser));
      }
    }
  }

  /** Workspaces donde el usuario es miembro del módulo, resueltos al iniciar sesión. */
  private readonly _workspacesAccesibles = signal<WorkspaceAccesible[]>([]);
  readonly workspacesAccesibles = this._workspacesAccesibles.asReadonly();

  /** Datos del usuario a la espera de que elija workspace. */
  private _pendingUser: LoginUser | null = null;
  private _pendingRememberMe = false;

  /**
   * Confirma el workspace elegido y entra. Se separa del login porque con
   * varios workspaces hay un paso intermedio: el usuario elige.
   */
  /**
   * Entra al workspace y navega al inicio.
   *
   * Se mantiene `loading` hasta que la navegación termina: guarda la sesión y
   * después navega, y esa navegación dispara la emisión del solution token, que
   * es un viaje al backend. Si se soltaba antes, el botón de iniciar sesión
   * volvía a habilitarse durante ese rato y admitía un segundo envío, que
   * repetía el login contra Firebase y el backend.
   */
  async entrarAlWorkspace(workspaceId: string): Promise<void> {
    const userData = this._pendingUser;
    if (!userData) return;

    this._loading.set(true);
    try {
      const usuario: LoginUser = { ...userData, workspaceId };
      const storage = this._pendingRememberMe ? localStorage : sessionStorage;

      this._token.set('solution-token-managed');
      this._user.set(usuario);
      this._workspaceId.set(workspaceId);

      storage.setItem('token', 'solution-token-managed');
      storage.setItem('user', JSON.stringify(usuario));
      storage.setItem('workspaceId', workspaceId);
      this.workspaceAccess.recordarUltimo(workspaceId);

      this._pendingUser = null;
      // La lista de opciones ya cumplió: dejarla viva hacía que el login
      // volviera a mostrar el selector en vez del formulario.
      this._workspacesAccesibles.set([]);
      await this.router.navigate(['/']);
    } finally {
      this._loading.set(false);
    }
  }

  /**
   * Entra con la sesión que trae un enlace de otra solución.
   *
   * El cambio de solución abre `https://mo-sit.myservy.com/?workspaceId=…`. La cookie de
   * sesión ya vale en todo el dominio, así que lo único que falta para no volver a pedir
   * credenciales es saber en qué workspace entrar. Sin esto el guard miraba sólo lo
   * guardado en este origen —vacío la primera vez que se llega por enlace— y devolvía al
   * usuario al formulario aunque su sesión estuviera viva.
   */
  async aplicarSesionDeEnlace(): Promise<boolean> {
    const workspaceId = this.consumirWorkspaceIdDeLaUrl();

    if (this.isAuthenticated()) {
      if (workspaceId) this.adoptarWorkspaceDelEnlace(workspaceId);
      return true;
    }

    return workspaceId ? this.restaurarSesionDesdeCookie(workspaceId) : false;
  }

  /**
   * Lee el `workspaceId` de la barra de direcciones y lo borra de ella, para que no
   * sobreviva a la siguiente navegación ni se quede pegado en un marcador.
   */
  private consumirWorkspaceIdDeLaUrl(): string | null {
    if (typeof window === 'undefined') return null;

    const pedido = new URLSearchParams(window.location.search).get('workspaceId')?.trim();
    if (!pedido) return null;

    const url = new URL(window.location.href);
    url.searchParams.delete('workspaceId');
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

    return pedido;
  }

  /**
   * La sesión ya estaba abierta y el enlace llega con otro workspace: manda el del
   * enlace, que es donde venía trabajando el usuario. No navega —esto corre dentro del
   * guard— y descarta el token cacheado, que lleva el rol del workspace anterior.
   */
  private adoptarWorkspaceDelEnlace(workspaceId: string): void {
    const usuario = this._user();
    if (!usuario || usuario.workspaceId === workspaceId) return;
    // Un enlace no puede dar acceso a un workspace que no es del usuario.
    if (usuario.workspaces?.length && !usuario.workspaces.some(w => w.id === workspaceId)) return;

    const actualizado = { ...usuario, workspaceId };
    this._user.set(actualizado);
    this._workspaceId.set(workspaceId);
    this.storage.setItem('user', JSON.stringify(actualizado));
    this.storage.setItem('workspaceId', workspaceId);
    this.workspaceAccess.recordarUltimo(workspaceId);
    this.solutionToken.clear();
  }

  /**
   * Canjea la cookie de sesión por el solution token y reconstruye el usuario.
   *
   * La sesión que llega por enlace vive sólo en la pestaña: quien entra así no eligió
   * "recordarme" en este navegador.
   */
  private async restaurarSesionDesdeCookie(workspaceId: string): Promise<boolean> {
    // El canje exige el workspace guardado: el interceptor lo manda en `x-workspace-id` y
    // el backend emite el token para ese workspace.
    sessionStorage.setItem('workspaceId', workspaceId);

    const token = await this.solutionToken.getToken().catch(() => null);
    const usuario = token ? await this.usuarioDeLaSesion(workspaceId) : null;
    if (!usuario) {
      sessionStorage.removeItem('workspaceId');
      this.solutionToken.clear();
      return false;
    }

    this._token.set('solution-token-managed');
    this._user.set(usuario);
    this._workspaceId.set(workspaceId);
    sessionStorage.setItem('token', 'solution-token-managed');
    sessionStorage.setItem('user', JSON.stringify(usuario));
    this.workspaceAccess.recordarUltimo(workspaceId);
    return true;
  }

  /**
   * El usuario de la sesión abierta, leído con la cookie. `login-google` no sirve
   * acá: pide credenciales de Firebase, que un enlace no trae.
   */
  private async usuarioDeLaSesion(workspaceId: string): Promise<LoginUser | null> {
    let respuesta: any;
    try {
      respuesta = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/api/v1/auth/me`, { withCredentials: true }),
      );
    } catch {
      return null;
    }

    const perfil = respuesta?.data?.user ?? respuesta?.data ?? respuesta?.user ?? respuesta;
    if (!perfil?.id && !perfil?.email) return null;

    const workspaces: WorkspaceInfo[] | undefined = perfil.workspaces;
    // Un enlace no puede dar acceso a un workspace que no es del usuario.
    if (workspaces?.length && !workspaces.some((w: WorkspaceInfo) => w.id === workspaceId)) return null;

    const nombre = (perfil.fullName ?? `${perfil.firstName ?? ''} ${perfil.lastName ?? ''}`).trim();

    return {
      id: perfil.id ?? perfil.googleId ?? '',
      email: perfil.email ?? '',
      firstName: perfil.firstName ?? '',
      lastName: perfil.lastName ?? '',
      fullName: nombre,
      role: perfil.role ?? { id: perfil.roleId ?? '', name: '' },
      roleId: perfil.roleId ?? perfil.role?.id ?? '',
      phone: perfil.phone ?? '',
      workspaceId,
      workspaces,
      profile: perfil.profile ?? null,
    };
  }

  /** Cancela la selección y vuelve al formulario. */
  cancelarSeleccionDeWorkspace(): void {
    this._pendingUser = null;
    this._workspacesAccesibles.set([]);
    this._error.set(null);
  }

  /**
   * Cambia el ID token de Firebase por la sesión de Console (la cookie), a
   * través de M.O. Antes se mandaba el uid a `login-external-id`, que Console
   * dejó de aceptar sin una sesión abierta; Transport hizo el mismo cambio.
   */
  private abrirSesionConGoogle(idToken: string): Promise<any> {
    return firstValueFrom(
      this.http.post<any>(`${environment.apiUrl}/api/v1/auth/login-google`, { idToken }, { withCredentials: true }),
    );
  }

  async login(email: string, password: string, rememberMe = false): Promise<void> {
    this._loading.set(true);
    this._error.set(null);

    try {
      // 1. Iniciar Firebase si no está iniciado
      const app = getApps().length === 0 ? initializeApp(environment.firebaseConfig) : getApp();
      const auth = getAuth(app);

      // 2. Auth con Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const fbUser = userCredential.user;

      // 3. Iniciar sesión en backend propio (M.O proxy intermediario)
      const loginRes = await this.abrirSesionConGoogle(await fbUser.getIdToken());

      const userData = loginRes.user || loginRes.data?.user || loginRes.data || loginRes;

      // 4. Extraer workspaces directamente de la respuesta del login-google
      const rawWorkspaces = loginRes.workspaces || loginRes.data?.workspaces || [];
      const workspaces = rawWorkspaces.map((w: any) => ({
        id: w.id,
        name: w.name,
        workspaceCode: w.workspaceCode,
        workspaceType: w.workspaceType
      }));
      userData.workspaces = workspaces;

      // 5. Quedarse solo con los workspaces donde el usuario es miembro del
      // módulo. La misma llamada devuelve su rol en cada uno.
      // `consultar` y no `resolver`: si la consulta falla tiene que llegar como
      // error, no como lista vacía, o se lee como "no tienes acceso".
      const accesibles = await this.workspaceAccess.consultarAccesibles(workspaces);
      if (accesibles.length === 0) {
        throw new Error('ACCESS_DENIED');
      }

      this._pendingUser = { ...userData, workspaces } as LoginUser;
      this._pendingRememberMe = rememberMe;
      this._workspacesAccesibles.set(accesibles);

      // Con uno solo no hay nada que preguntar; con varios decide el usuario.
      // En ese caso la pantalla pasa al selector y el formulario tiene que
      // quedar utilizable, así que recién ahí se suelta `loading`.
      if (accesibles.length === 1) {
        await this.entrarAlWorkspace(accesibles[0].id);
        return;
      }

      this._loading.set(false);

    } catch (error: any) {
      this._loading.set(false);
      
      // Se mantiene el error original en consola para auditoría/QA
      console.error('Detalle de error en inicio de sesión:', error);

      // Antes todo lo que no fuera falta de membresía salía como "Usuario o
      // contraseña inválidos", incluido un límite de peticiones o el servicio
      // caído: la gente reintentaba, y cada reintento estiraba el bloqueo.
      this._error.set(mensajeDeErrorDeLogin(error));
    }
  }

  /**
   * Cambia de workspace sin recargar la aplicación. Hay que limpiar el token
   * cacheado: el rol, las sedes y los permisos son distintos en cada workspace,
   * y si no se descartan el usuario sigue operando con los del anterior.
   */
  async switchWorkspace(newWorkspaceId: string): Promise<void> {
    const currentUser = this._user();
    if (!currentUser || currentUser.workspaceId === newWorkspaceId) return;

    const updatedUser = { ...currentUser, workspaceId: newWorkspaceId };
    this._user.set(updatedUser);
    this._workspaceId.set(newWorkspaceId);
    this.storage.setItem('user', JSON.stringify(updatedUser));
    this.storage.setItem('workspaceId', newWorkspaceId);
    this.workspaceAccess.recordarUltimo(newWorkspaceId);

    this.solutionToken.clear();
    await this.solutionToken.getToken();

    // Se queda donde está si el rol nuevo se lo permite; si no, a su inicio.
    const rutaActual = this.router.url.split('?')[0];
    const capacidadDeLaRuta = CAPACIDAD_POR_RUTA[rutaActual];
    const puedeSeguir = !capacidadDeLaRuta || this.solutionToken.puede(capacidadDeLaRuta);

    if (puedeSeguir) {
      // Fuerza a las pantallas a releer con el workspace nuevo.
      await this.router.navigateByUrl('/', { skipLocationChange: true });
      await this.router.navigateByUrl(rutaActual);
    } else {
      await this.router.navigateByUrl(rutaInicial(this.solutionToken));
    }
  }

  logout(): void {
    // Sin esto, el rol, las sedes, las capacidades y el token del usuario
    // anterior sobreviven en el singleton: quien entrara después sin recargar
    // la página heredaba la sesión del que se fue.
    this.solutionToken.clear();
    this._token.set(null);
    this._user.set(null);
    this._workspaceId.set(GENERIC_WORKSPACE_ID);
    // Con varios workspaces, la lista sobrevivía al cierre de sesión y el
    // login arrancaba en el selector en vez del formulario.
    this._workspacesAccesibles.set([]);
    this._pendingUser = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('workspaceId');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('workspaceId');
    this.router.navigate(['/login']);
  }

  async getOrCreateFirebaseUser(email: string, password?: string): Promise<string> {
    const secondaryApp = initializeApp(
      environment.firebaseConfig,
      `user-creation-${Date.now()}`
    );

    try {
      const secondaryAuth = getAuth(secondaryApp);
      try {
        const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password ?? 'temporal123');
        return credential.user.uid;
      } catch (error: unknown) {
        if (error instanceof FirebaseError && error.code === 'auth/email-already-in-use') {
          const credential = await signInWithEmailAndPassword(secondaryAuth, email, password ?? 'temporal123');
          return credential.user.uid;
        }
        throw error;
      }
    } finally {
      await deleteApp(secondaryApp);
    }
  }

  async resetPassword(email: string, language: string = 'es'): Promise<void> {
    const app = getApps().length === 0 ? initializeApp(environment.firebaseConfig) : getApp();
    const functions = getFunctions(app, 'us-central1');
    const requestReset = httpsCallable(functions, 'generateCustomResetLink');
    
    await requestReset({ 
      email: email.trim(), 
      continueUrl: (environment as any).passwordResetContinueUrl || environment.baseUrl, 
      language 
    });
  }
}
